import React, { useState } from 'react';
import { LogIn, Loader, Shield, User } from 'lucide-react';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001';

const Login = ({ onLogin }) => {
  const [password, setPassword] = useState('');
  const [username, setUsername] = useState('');
  const [isAdminLogin, setIsAdminLogin] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      const response = await fetch(`${API_URL}/api/login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ 
          password,
          username: isAdminLogin ? username : undefined 
        }),
      });

      const data = await response.json();

      if (response.ok) {
        onLogin(data.token, data.isAdmin);
      } else {
        setError(data.error || 'Login fehlgeschlagen');
      }
    } catch (err) {
      setError('Verbindung zum Server fehlgeschlagen');
    } finally {
      setLoading(false);
    }
  };

  const toggleLoginMode = () => {
    setIsAdminLogin(!isAdminLogin);
    setError('');
    setPassword('');
    setUsername('');
  };

  const styles = {
    container: {
      minHeight: '100vh',
      background: 'linear-gradient(135deg, #eff6ff 0%, #eef2ff 100%)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '16px',
    },
    card: {
      backgroundColor: 'white',
      borderRadius: '12px',
      boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
      padding: '32px',
      maxWidth: '400px',
      width: '100%',
    },
    header: {
      textAlign: 'center',
      marginBottom: '32px',
    },
    iconContainer: {
      width: '80px',
      height: '80px',
      borderRadius: '50%',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      margin: '0 auto 16px auto',
      backgroundColor: isAdminLogin ? '#f59e0b' : '#4f46e5',
      transition: 'background-color 0.3s',
    },
    title: {
      fontSize: '1.875rem',
      fontWeight: 'bold',
      color: '#1f2937',
      marginBottom: '8px',
    },
    subtitle: {
      color: '#6b7280',
    },
    form: {
      display: 'flex',
      flexDirection: 'column',
      gap: '24px',
    },
    inputGroup: {
      display: 'flex',
      flexDirection: 'column',
    },
    label: {
      fontSize: '0.875rem',
      fontWeight: '500',
      color: '#374151',
      marginBottom: '8px',
      display: 'flex',
      alignItems: 'center',
      gap: '8px',
    },
    input: {
      width: '100%',
      padding: '12px 16px',
      border: '1px solid #d1d5db',
      borderRadius: '8px',
      fontSize: '1rem',
      outline: 'none',
      transition: 'border-color 0.2s, box-shadow 0.2s',
      boxSizing: 'border-box',
    },
    errorBox: {
      backgroundColor: '#fef2f2',
      border: '1px solid #fecaca',
      color: '#b91c1c',
      padding: '12px 16px',
      borderRadius: '8px',
    },
    button: {
      width: '100%',
      padding: '12px',
      borderRadius: '8px',
      fontWeight: '600',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      gap: '8px',
      border: 'none',
      cursor: 'pointer',
      fontSize: '1rem',
      color: 'white',
      backgroundColor: loading || !password || (isAdminLogin && !username) 
        ? '#9ca3af' 
        : (isAdminLogin ? '#f59e0b' : '#4f46e5'),
      transition: 'background-color 0.2s',
    },
    toggleContainer: {
      marginTop: '24px',
      textAlign: 'center',
    },
    toggleButton: {
      background: 'none',
      border: 'none',
      fontSize: '0.875rem',
      color: '#6b7280',
      cursor: 'pointer',
      display: 'inline-flex',
      alignItems: 'center',
      gap: '8px',
      padding: '8px',
      transition: 'color 0.2s',
    },
  };

  return (
    <div style={styles.container}>
      <div style={styles.card}>
        <div style={styles.header}>
          <div style={styles.iconContainer}>
            {isAdminLogin ? (
              <Shield color="white" size={40} />
            ) : (
              <LogIn color="white" size={40} />
            )}
          </div>
          <h1 style={styles.title}>Volleyball Kalender</h1>
          <p style={styles.subtitle}>
            {isAdminLogin ? 'Admin-Anmeldung' : 'Melde dich an um fortzufahren'}
          </p>
        </div>

        <form onSubmit={handleSubmit} style={styles.form}>
          {isAdminLogin && (
            <div style={styles.inputGroup}>
              <label style={styles.label}>
                <User size={16} />
                Benutzername
              </label>
              <input
                type="text"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                required
                placeholder="Admin-Benutzername"
                style={styles.input}
              />
            </div>
          )}

          <div style={styles.inputGroup}>
            <label style={styles.label}>
              Passwort
            </label>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              placeholder={isAdminLogin ? 'Admin-Passwort' : 'Passwort eingeben'}
              style={styles.input}
            />
          </div>

          {error && (
            <div style={styles.errorBox}>
              {error}
            </div>
          )}

          <button
            type="submit"
            disabled={loading || !password || (isAdminLogin && !username)}
            style={styles.button}
          >
            {loading ? (
              <>
                <Loader className="animate-spin" size={20} />
                Anmeldung läuft...
              </>
            ) : (
              <>
                {isAdminLogin ? <Shield size={20} /> : <LogIn size={20} />}
                {isAdminLogin ? 'Als Admin anmelden' : 'Anmelden'}
              </>
            )}
          </button>
        </form>

        <div style={styles.toggleContainer}>
          <button
            type="button"
            onClick={toggleLoginMode}
            style={styles.toggleButton}
            onMouseOver={(e) => e.target.style.color = '#374151'}
            onMouseOut={(e) => e.target.style.color = '#6b7280'}
          >
            {isAdminLogin ? (
              <>
                <LogIn size={16} />
                Zurück zur normalen Anmeldung
              </>
            ) : (
              <>
                <Shield size={16} />
                Als Admin anmelden
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default Login;
